/**
 * 人物卡片组件
 * 点击人物名称或头像后打开，显示该人物的详细信息
 */

import { useState, useCallback } from 'react'
import type { Character, CharacterIdentity, CharacterStatus, CharacterTrait } from '../types/character'
import './CharacterCard.css'

interface CharacterCardProps {
  character: Character
  onClose?: () => void
}

export function CharacterCard({ character, onClose }: CharacterCardProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'abilities' | 'relations' | 'history'>('info')

  const handleClose = useCallback(() => {
    onClose?.()
  }, [onClose])

  // 获取身份标签
  const getIdentityLabel = (identity: CharacterIdentity): string => {
    const labels: Record<CharacterIdentity, string> = {
      royal: '王室宗亲',
      official: '朝臣官员',
      commoner: '平民百姓',
      general: '将领',
      scholar: '学者谋士',
      merchant: '商人',
    }
    return labels[identity] || identity
  }

  // 获取状态标签
  const getStatusLabel = (status: CharacterStatus): string => {
    const labels: Record<CharacterStatus, string> = {
      active: '活跃',
      sick: '生病',
      imprisoned: '囚禁',
      exiled: '流放',
      retired: '退休',
      deceased: '去世',
    }
    return labels[status] || status
  }

  // 获取状态颜色
  const getStatusColor = (status: CharacterStatus): string => {
    const colors: Record<CharacterStatus, string> = {
      active: '#3fb950',
      sick: '#d29922',
      imprisoned: '#f85149',
      exiled: '#58a6ff',
      retired: '#8b949e',
      deceased: '#da3633',
    }
    return colors[status] || '#8b949e'
  }

  // 获取特质标签
  const getTraitLabel = (trait: CharacterTrait): string => {
    const labels: Record<CharacterTrait, string> = {
      brave: '勇敢',
      cautious: '谨慎',
      cunning: '狡猾',
      honest: '诚实',
      ambitious: '野心勃勃',
      loyal: '忠诚',
      treacherous: '奸诈',
      generous: '慷慨',
      stingy: '吝啬',
      wise: '睿智',
      foolish: '愚蠢',
      charismatic: '有魅力',
      cruel: '残暴',
      benevolent: '仁慈',
    }
    return labels[trait] || trait
  }

  // 获取特质颜色
  const getTraitColor = (trait: CharacterTrait): string => {
    const positiveTraits: CharacterTrait[] = ['brave', 'honest', 'loyal', 'generous', 'wise', 'charismatic', 'benevolent']
    const negativeTraits: CharacterTrait[] = ['cunning', 'treacherous', 'stingy', 'foolish', 'cruel']
    if (positiveTraits.includes(trait)) return '#3fb950'
    if (negativeTraits.includes(trait)) return '#f85149'
    return '#d29922'
  }

  // 计算年龄
  const calculateAge = (): number => {
    if (!character.birthDate) return 0
    const birthYear = character.birthDate.year
    const currentYear = new Date().getFullYear()
    return Math.abs(currentYear - birthYear)
  }

  // 计算平均能力值
  const calculateAverageAbility = (): number => {
    const { intelligence, charisma, military, administration, diplomacy, intrigue } = character.abilities
    return Math.round((intelligence + charisma + military + administration + diplomacy + intrigue) / 6)
  }

  return (
    <div className="character-card-overlay" onClick={handleClose}>
      <div className="character-card" onClick={e => e.stopPropagation()}>
        {/* 卡片头部 */}
        <div className="card-header">
          <div className="header-main">
            <div className="character-avatar">
              <span className="avatar-icon">
                {character.gender === 'male' ? '👤' : '👩'}
              </span>
            </div>
            <div className="header-info">
              <h2 className="character-name">{character.name}</h2>
              <div className="character-tags">
                <span className="tag identity">{getIdentityLabel(character.identity)}</span>
                <span
                  className="tag status"
                  style={{ backgroundColor: `${getStatusColor(character.status)}20`, color: getStatusColor(character.status) }}
                >
                  {getStatusLabel(character.status)}
                </span>
                {character.position && (
                  <span className="tag position">{character.position.title}</span>
                )}
              </div>
            </div>
          </div>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        {/* 标签页 */}
        <div className="card-tabs">
          <button
            className={`tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            基本信息
          </button>
          <button
            className={`tab ${activeTab === 'abilities' ? 'active' : ''}`}
            onClick={() => setActiveTab('abilities')}
          >
            能力属性
          </button>
          <button
            className={`tab ${activeTab === 'relations' ? 'active' : ''}`}
            onClick={() => setActiveTab('relations')}
          >
            关系网络
          </button>
          <button
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            历史记录
          </button>
        </div>

        {/* 内容区域 */}
        <div className="card-content">
          {/* 基本信息 */}
          {activeTab === 'info' && (
            <div className="tab-content info-tab">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">姓名</span>
                  <span className="info-value">{character.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">性别</span>
                  <span className="info-value">{character.gender === 'male' ? '男' : '女'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">年龄</span>
                  <span className="info-value">{calculateAge()} 岁</span>
                </div>
                <div className="info-item">
                  <span className="info-label">身份</span>
                  <span className="info-value">{getIdentityLabel(character.identity)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">状态</span>
                  <span className="info-value" style={{ color: getStatusColor(character.status) }}>
                    {getStatusLabel(character.status)}
                  </span>
                </div>
                {character.position && (
                  <>
                    <div className="info-item">
                      <span className="info-label">职位</span>
                      <span className="info-value">{character.position.title}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">品级</span>
                      <span className="info-value">{character.position.rank} 品</span>
                    </div>
                    {character.position.department && (
                      <div className="info-item">
                        <span className="info-label">部门</span>
                        <span className="info-value">{character.position.department}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="info-item">
                  <span className="info-label">忠诚度</span>
                  <div className="info-value-with-bar">
                    <span className="info-value">{character.loyalty}</span>
                    <div className="mini-bar">
                      <div
                        className="mini-fill loyalty"
                        style={{ width: `${character.loyalty}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="info-item">
                  <span className="info-label">声望</span>
                  <div className="info-value-with-bar">
                    <span className="info-value">{character.reputation}</span>
                    <div className="mini-bar">
                      <div
                        className="mini-fill reputation"
                        style={{ width: `${character.reputation}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="info-item">
                  <span className="info-label">平均能力</span>
                  <span className="info-value highlight">{calculateAverageAbility()}</span>
                </div>
              </div>

              {/* 性格特质 */}
              {character.traits.length > 0 && (
                <div className="traits-section">
                  <h4>性格特质</h4>
                  <div className="traits-list">
                    {character.traits.map(trait => (
                      <span
                        key={trait}
                        className="trait-tag"
                        style={{ backgroundColor: `${getTraitColor(trait)}20`, color: getTraitColor(trait) }}
                      >
                        {getTraitLabel(trait)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 能力属性 */}
          {activeTab === 'abilities' && (
            <div className="tab-content abilities-tab">
              <div className="abilities-grid">
                <div className="ability-item">
                  <div className="ability-header">
                    <span className="ability-name">智力</span>
                    <span className="ability-value">{character.abilities.intelligence}</span>
                  </div>
                  <div className="ability-bar">
                    <div
                      className="ability-fill intelligence"
                      style={{ width: `${character.abilities.intelligence}%` }}
                    />
                  </div>
                  <span className="ability-desc">谋略、计策</span>
                </div>

                <div className="ability-item">
                  <div className="ability-header">
                    <span className="ability-name">魅力</span>
                    <span className="ability-value">{character.abilities.charisma}</span>
                  </div>
                  <div className="ability-bar">
                    <div
                      className="ability-fill charisma"
                      style={{ width: `${character.abilities.charisma}%` }}
                    />
                  </div>
                  <span className="ability-desc">说服、外交</span>
                </div>

                <div className="ability-item">
                  <div className="ability-header">
                    <span className="ability-name">武力</span>
                    <span className="ability-value">{character.abilities.military}</span>
                  </div>
                  <div className="ability-bar">
                    <div
                      className="ability-fill military"
                      style={{ width: `${character.abilities.military}%` }}
                    />
                  </div>
                  <span className="ability-desc">战斗、统帅</span>
                </div>

                <div className="ability-item">
                  <div className="ability-header">
                    <span className="ability-name">政务</span>
                    <span className="ability-value">{character.abilities.administration}</span>
                  </div>
                  <div className="ability-bar">
                    <div
                      className="ability-fill administration"
                      style={{ width: `${character.abilities.administration}%` }}
                    />
                  </div>
                  <span className="ability-desc">治理、内政</span>
                </div>

                <div className="ability-item">
                  <div className="ability-header">
                    <span className="ability-name">外交</span>
                    <span className="ability-value">{character.abilities.diplomacy}</span>
                  </div>
                  <div className="ability-bar">
                    <div
                      className="ability-fill diplomacy"
                      style={{ width: `${character.abilities.diplomacy}%` }}
                    />
                  </div>
                  <span className="ability-desc">谈判、联盟</span>
                </div>

                <div className="ability-item">
                  <div className="ability-header">
                    <span className="ability-name">谋略</span>
                    <span className="ability-value">{character.abilities.intrigue}</span>
                  </div>
                  <div className="ability-bar">
                    <div
                      className="ability-fill intrigue"
                      style={{ width: `${character.abilities.intrigue}%` }}
                    />
                  </div>
                  <span className="ability-desc">间谍、暗杀</span>
                </div>
              </div>

              {/* 能力雷达图区域（预留） */}
              <div className="ability-summary">
                <div className="summary-item">
                  <span className="summary-label">总能力值</span>
                  <span className="summary-value">
                    {character.abilities.intelligence +
                      character.abilities.charisma +
                      character.abilities.military +
                      character.abilities.administration +
                      character.abilities.diplomacy +
                      character.abilities.intrigue}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">平均能力</span>
                  <span className="summary-value">{calculateAverageAbility()}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">最高能力</span>
                  <span className="summary-value">
                    {Math.max(
                      character.abilities.intelligence,
                      character.abilities.charisma,
                      character.abilities.military,
                      character.abilities.administration,
                      character.abilities.diplomacy,
                      character.abilities.intrigue
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* 关系网络 */}
          {activeTab === 'relations' && (
            <div className="tab-content relations-tab">
              <div className="relations-grid">
                {character.relations.fatherId && (
                  <div className="relation-card">
                    <span className="relation-type">父亲</span>
                    <span className="relation-target">{character.relations.fatherId}</span>
                  </div>
                )}
                {character.relations.motherId && (
                  <div className="relation-card">
                    <span className="relation-type">母亲</span>
                    <span className="relation-target">{character.relations.motherId}</span>
                  </div>
                )}
                {character.relations.spouseId && (
                  <div className="relation-card">
                    <span className="relation-type">配偶</span>
                    <span className="relation-target">{character.relations.spouseId}</span>
                  </div>
                )}
                {character.relations.childrenIds.length > 0 && (
                  <div className="relation-card">
                    <span className="relation-type">子女</span>
                    <span className="relation-target">{character.relations.childrenIds.length} 人</span>
                  </div>
                )}
                {character.relations.siblingIds.length > 0 && (
                  <div className="relation-card">
                    <span className="relation-type">兄弟姐妹</span>
                    <span className="relation-target">{character.relations.siblingIds.length} 人</span>
                  </div>
                )}
                {character.relations.friendIds.length > 0 && (
                  <div className="relation-card">
                    <span className="relation-type">朋友</span>
                    <span className="relation-target">{character.relations.friendIds.length} 人</span>
                  </div>
                )}
                {character.relations.enemyIds.length > 0 && (
                  <div className="relation-card enemy">
                    <span className="relation-type">敌人</span>
                    <span className="relation-target">{character.relations.enemyIds.length} 人</span>
                  </div>
                )}
              </div>

              {Object.values(character.relations).every(v =>
                Array.isArray(v) ? v.length === 0 : !v
              ) && (
                <div className="empty-relations">
                  <span className="empty-icon">👤</span>
                  <p>暂无关系记录</p>
                </div>
              )}
            </div>
          )}

          {/* 历史记录 */}
          {activeTab === 'history' && (
            <div className="tab-content history-tab">
              {character.history.length > 0 ? (
                <div className="history-timeline">
                  {character.history.map(event => (
                    <div key={event.id} className="history-item">
                      <div className="history-marker">
                        <div className="history-dot" />
                        <div className="history-line" />
                      </div>
                      <div className="history-content">
                        <div className="history-header">
                          <span className="history-title">{event.title}</span>
                          <span className="history-date">
                            {event.date.year}年{event.date.month}月{event.date.day}日
                          </span>
                        </div>
                        <p className="history-desc">{event.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-history">
                  <span className="empty-icon">📜</span>
                  <p>暂无历史记录</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
